import {
  ActionIcon,
  Tooltip,
  CopyButton as MantineCopyButton,
  useMantineTheme,
} from '@mantine/core';
import { forwardRef } from 'react';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface CopyButtonProps {
  value: string;
  timeout?: number;
  children?: (props: { copied: boolean; copy: () => void }) => React.ReactNode;
  label?: string;
  copiedLabel?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'subtle' | 'filled' | 'light' | 'outline' | 'transparent';
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
}

const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      value,
      timeout = 2000,
      children,
      label = 'Copy',
      copiedLabel = 'Copied!',
      size = 'sm',
      variant = 'subtle',
      gradientVariant = 'primary',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    return (
      <MantineCopyButton value={value} timeout={timeout}>
        {({ copied, copy }) =>
          children ? (
            children({ copied, copy })
          ) : (
            <Tooltip label={copied ? copiedLabel : label} withArrow>
              <ActionIcon
                ref={ref}
                variant={variant}
                color={copied ? 'teal' : gradient?.from}
                onClick={copy}
                size={size}
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )
        }
      </MantineCopyButton>
    );
  }
);

CopyButton.displayName = 'CopyButton';

export default CopyButton;
